/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { createContext, useEffect, useMemo, useRef, useState } from 'react';

import useRouter from 'js/lib/useRouter';

import { Grid, PageGridContainer, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { css as cuiCss, placeholder } from '@coursera/coursera-ui';

import ClipEngagementActions from 'bundles/clips/components/ClipEngagementActions';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { ClipMetadata, ClipMetadataLoading } from 'bundles/clips/components/ClipMetadata';
import { ClipsErrorMessage } from 'bundles/clips/components/ClipsErrorMessage';
import { ClipsNavBar } from 'bundles/clips/components/ClipsNavBar';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import ClipsVideoPlayer from 'bundles/clips/components/ClipsVideoPlayer';
import { EnrollButtonErrorNotification } from 'bundles/clips/components/EnrollButtonErrorNotification';
import { MobileCourseCard } from 'bundles/clips/components/MobileCourseCard';
import { SFCEnrollmentNotification } from 'bundles/clips/components/short-form-content/SFCEnrollmentNotifications';
import type { NotificationType } from 'bundles/clips/components/short-form-content/SFCEnrollmentNotifications';
import type { OnDemandVideos, ProgramSuggestion } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import { useSelectedProgram } from 'bundles/clips/components/short-form-content/useSelectedProgram';
import {
  convertVideoAssetsToOnDemandVideo,
  hasVideosFiltered,
} from 'bundles/clips/components/short-form-content/utils';
import type {
  Course,
  OverridePartnerLogos,
  Partner,
  Rating,
  VLPItem,
  VLPLesson,
  XDPData,
} from 'bundles/clips/types/vlpSharedTypes';
import useClipsVideoAssets from 'bundles/clips/utils/useClipsVideoAssets';
import useEnrollIntoCourseMutation from 'bundles/clips/utils/useEnrollIntoCourseMutation';
import { useGetTrimRangeQuery } from 'bundles/clips/utils/useGetTrimRange';
import { NotFound } from 'bundles/common/components/withNotFound';
import FixedAspectRatioBox from 'bundles/general-video-player/components/FixedAspectRatioBox';
import type { VideoPlayerRef } from 'bundles/general-video-player/components/VideoPlayer';
import { TrackList } from 'bundles/interactive-transcript';
import { buildTracks } from 'bundles/interactive-transcript/utils/TrackUtils';
import useIsMobile from 'bundles/unified-common/hooks/useIsMobile';
import { VideoTrimmingContextProvider } from 'bundles/video-trim';

type PropsFromCaller = {
  course: Course;
  courseId: string;
  courseSlug: string;
  programSlug?: string;
  itemId: string;
  overridePartnerLogos?: OverridePartnerLogos;
  currentLesson?: VLPLesson;
  currentVideoItem: VLPItem;
  lectureItems: Array<VLPItem>;
  xdpApiData: XDPData;
  skills: Array<string>;
  views?: number;
  likes?: number;
  preferredAvailableForEnrollmentProgram?: ProgramSuggestion;
  accessProvidedViaProgram?: ProgramSuggestion;
  userEnrolledIntoCourse?: boolean;
  lessons: Array<VLPLesson>;
  gqlLoading: boolean;
  summary: string | null;
};

export type Props = PropsFromCaller;

const clipsVideoRootContainer = css`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(auto, 970px) 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'video navbar'
    'metadata navbar';
  column-gap: 36px;
  row-gap: 16px;
  margin-bottom: var(--cds-spacing-300);

  ${breakpoints.down('md')} {
    grid-template-columns: minmax(auto, 970px) 1fr;
    grid-template-areas:
      'video video'
      'metadata navbar';
  }

  ${breakpoints.down('xs')} {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    gap: 0;
    grid-template-areas:
      'video'
      'navbar'
      'metadata';
  }
`;

const clipsVideoPlayerContainerLoading = (theme: Theme) =>
  css({
    [breakpoints.down('xs')]: {
      marginLeft: '-16px',
      marginRight: '-16px',
    },
  });

const clipsBottomSection = (theme: Theme) =>
  css({
    display: 'flex',
    gap: '32px',
    [breakpoints.down('xs')]: {
      flexDirection: 'column',
      marginTop: '0',
    },
  });

const enrollButtonErrorNotification = (theme: Theme) =>
  css({
    marginTop: 'var(--cds-spacing-200)',
  });

export type VideoContextState = {
  isPlaying: boolean;
  duration: number;
  setIsPlaying: (isPlaying: boolean) => void;
  setDuration: (duration: number) => void;
  videoPlayer: VideoPlayerRef | null;
};

// VideoContext to hold video state so that we don't have to pass it down through multiple components
export const VideoContext = createContext<VideoContextState>({
  isPlaying: false,
  duration: 0,
  setIsPlaying: () => null,
  setDuration: () => null,
  videoPlayer: null,
});

export const ClipsVideoRootContainerLoading = ({ clipsNavBar }: ClipsVideoRootContainerLoadingProps) => {
  const theme = useTheme();

  return (
    <PageGridContainer>
      <Grid item xs={12} css={clipsVideoRootContainer} data-testid="clips-video-root-container-loading">
        <div css={{ gridArea: 'video' }}>
          <div css={clipsVideoPlayerContainerLoading(theme)} className={cuiCss(placeholder.styles.shimmer).className}>
            <FixedAspectRatioBox aspectRatio={16 / 9}>
              <div>&nbsp;</div>
            </FixedAspectRatioBox>
          </div>
        </div>

        <div css={{ gridArea: 'navbar' }}>{clipsNavBar}</div>

        <div css={{ gridArea: 'metadata' }}>
          <ClipMetadataLoading />
        </div>
      </Grid>
    </PageGridContainer>
  );
};

export function ClipsVideoRootContainer({
  course,
  courseId,
  courseSlug,
  overridePartnerLogos,
  currentLesson,
  currentVideoItem,
  lectureItems,
  xdpApiData,
  views,
  likes,
  skills,
  preferredAvailableForEnrollmentProgram,
  accessProvidedViaProgram,
  userEnrolledIntoCourse,
  lessons,
  programSlug,
  itemId,
  gqlLoading,
  summary,
}: PropsFromCaller) {
  const router = useRouter();
  const { trimRangeData, loading: isTrimRangeLoading } = useGetTrimRangeQuery({ itemId, courseId });
  const startAtString = router.location.query.startAt;
  const startAtVal = startAtString ? parseInt(startAtString as string, 10) : undefined;

  const startAt = useMemo(() => {
    if (startAtVal) {
      return startAtVal >= trimRangeData.startTimeSeconds ? startAtVal : trimRangeData.startTimeSeconds;
    }
    return startAtVal;
  }, [startAtVal, trimRangeData.startTimeSeconds]);

  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  const isMobile = useIsMobile();
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<number>(0);
  const [showEnrollmentButtonError, setShowEnrollmentButtonError] = useState(false);

  const [enrollIntoCourseMutation, { loading: enrollmentLoading }] = useEnrollIntoCourseMutation();
  const [notificationType, setNotifcationType] = useState<NotificationType | undefined>();

  const [onDemandVideos, setOnDemandVideos] = useState<OnDemandVideos | undefined>(undefined);

  const { data: videoAssets, loading: isClipsVideoAssetsLoading } = useClipsVideoAssets({
    programSlug,
    courseId,
    itemId,
  });

  const isVideoAssetsLoading = isClipsVideoAssetsLoading || isTrimRangeLoading;

  const [justEnrolledThroughMutation, setJustEnrolledThroughMutation] = useState(false);

  const { programId, organizationId, loading: selectedProgramLoading } = useSelectedProgram();

  useEffect(() => {
    if (videoAssets && !isVideoAssetsLoading) {
      setOnDemandVideos(convertVideoAssetsToOnDemandVideo(videoAssets?.ShortFormContent?.queryVideoData?.videoAssets));
    }
  }, [videoAssets, isVideoAssetsLoading]);

  const isLoading = (gqlLoading || isVideoAssetsLoading || selectedProgramLoading) ?? true;

  useEffect(() => {
    if (!isLoading && onDemandVideos) {
      if (justEnrolledThroughMutation) {
        setNotifcationType('enrollmentSuccess');
      } else if (hasVideosFiltered(onDemandVideos)) {
        setNotifcationType('enrollmentInformation');
      } else {
        setNotifcationType(undefined);
      }
    }
  }, [isLoading, onDemandVideos, justEnrolledThroughMutation]);

  const clipsNavBar = (
    <ClipsNavBar
      course={course}
      lectureItems={lectureItems}
      lessons={lessons}
      currentLesson={currentLesson}
      currentVideoItem={currentVideoItem}
      skills={skills}
      programId={accessProvidedViaProgram?.id}
      isLoading={isLoading}
    />
  );

  if (isLoading) {
    return <ClipsVideoRootContainerLoading clipsNavBar={clipsNavBar} />;
  }
  if (!onDemandVideos) {
    return <NotFound description={<ClipsErrorMessage />} />;
  }

  // build tracks
  const tracks = (onDemandVideos?.subtitlesVtt && buildTracks(onDemandVideos?.subtitlesVtt)) ?? new TrackList([]);

  const {
    xdpMetadata: {
      cdpMetadata: { name: courseName, partners, ratings },
    },
  } = xdpApiData;
  const isStandaloneCourse = course?.isStandaloneCourse;

  const handleEnrollmentErrorNotificationDismissal = () => {
    setShowEnrollmentButtonError(false);
  };

  const handleEnroll = () => {
    const input = {
      courseId: courseId || '',
      itemId: currentVideoItem.id || '',
      programId: preferredAvailableForEnrollmentProgram?.id || '',
    };

    return enrollIntoCourseMutation({ variables: input })
      .then((res) => {
        // update the onDemandVideos after enrollment
        if (res.data?.ShortFormContent_enrollEnterpriseUserIntoCourse?.success) {
          setJustEnrolledThroughMutation(true);
          setOnDemandVideos(
            convertVideoAssetsToOnDemandVideo(res.data?.ShortFormContent_enrollEnterpriseUserIntoCourse?.videoAssets)
          );
          videoPlayerRef.current?.reloadSources();
        }
      })
      .catch(() => {
        setNotifcationType('enrollmentError');
      });
  };

  return (
    <VideoContext.Provider
      value={{ isPlaying, setIsPlaying, duration, setDuration, videoPlayer: videoPlayerRef.current }}
    >
      <VideoTrimmingContextProvider
        trimmedStartTimeSeconds={trimRangeData.startTimeSeconds}
        trimmedDurationSeconds={trimRangeData.trimmedDurationSeconds}
      >
        <PageGridContainer>
          <Grid item xs={12} css={clipsVideoRootContainer} data-testid="clips-video-root-container">
            <div css={{ gridArea: 'video' }}>
              <ClipsVideoPlayer
                ref={videoPlayerRef}
                courseId={courseId}
                courseName={courseName}
                courseSlug={courseSlug}
                videoItem={currentVideoItem}
                onDemandVideos={onDemandVideos}
                tracks={tracks}
                lectureItems={lectureItems}
                // this seems to be cast because partners is nullable
                partners={partners as Array<Partner>}
                justEnrolledThroughMutation={justEnrolledThroughMutation}
                // this seems to be cast because ratings can be undefined
                ratings={ratings as Rating}
                skills={skills}
                accessProvidedViaProgram={accessProvidedViaProgram}
                programId={programId}
                organizationId={organizationId}
                startAt={startAt}
                onEnroll={handleEnroll}
                isEnrolling={enrollmentLoading}
              />
              <SFCEnrollmentNotification overlayType={notificationType} courseId={courseId} />
            </div>

            <div css={{ gridArea: 'navbar' }}>{clipsNavBar}</div>

            <div css={{ gridArea: 'metadata' }}>
              {showEnrollmentButtonError && (
                <div css={enrollButtonErrorNotification}>
                  <EnrollButtonErrorNotification onDismiss={handleEnrollmentErrorNotificationDismissal} />
                </div>
              )}
              <div css={clipsBottomSection}>
                <ClipMetadata
                  summary={summary}
                  course={course}
                  videoItem={currentVideoItem}
                  overridePartnerLogos={overridePartnerLogos}
                  // this seems to be cast because partners is nullableLogos}
                  partners={partners as Array<Partner>}
                  views={views}
                  skills={skills}
                  durationMs={currentVideoItem.timeCommitment}
                  tracks={tracks}
                  lectureItems={lectureItems}
                  accessProvidedViaProgram={accessProvidedViaProgram}
                  userEnrolledIntoCourse={userEnrolledIntoCourse}
                  justEnrolledThroughMutation={justEnrolledThroughMutation}
                  setShowEnrollmentButtonError={setShowEnrollmentButtonError}
                  clipEngagementActions={
                    <ClipEngagementActions
                      courseId={courseId}
                      itemFeedbackType="lecture"
                      itemId={currentVideoItem.id}
                      likes={likes}
                    />
                  }
                />
              </div>
            </div>
            {isMobile && !isStandaloneCourse && (
              <MobileCourseCard
                // this seems to be cast because partners is nullable
                partners={partners as Array<Partner>}
                course={course}
                accessProvidedViaProgram={accessProvidedViaProgram}
                onDemandVideos={onDemandVideos}
                justEnrolledThroughMutation={justEnrolledThroughMutation}
                userEnrolledIntoCourse={userEnrolledIntoCourse}
                itemId={currentVideoItem.id}
                setShowEnrollmentButtonError={setShowEnrollmentButtonError}
              />
            )}
          </Grid>
        </PageGridContainer>
      </VideoTrimmingContextProvider>
    </VideoContext.Provider>
  );
}

type ClipsVideoRootContainerLoadingProps = {
  clipsNavBar: JSX.Element;
};
